// A network's release version is a `major.minor.patch` string (e.g. "3.23.0").
// KBE-544: persisted and stamped at release, independent of the free-text name,
// so consumers can filter/compare without regex-parsing names.

export const NETWORK_VERSION_REGEX = /^\d+\.\d+\.\d+$/

export function isValidNetworkVersion(value: string): boolean {
  return NETWORK_VERSION_REGEX.test(value.trim())
}

/** Parses "major.minor.patch" into `[major, minor, patch]`, or null when the
 * input isn't a valid version. */
export function parseNetworkVersion(
  value: string | null | undefined,
): [number, number, number] | null {
  if (!value || !isValidNetworkVersion(value)) return null
  const [major, minor, patch] = value
    .trim()
    .split('.')
    .map((part) => parseInt(part, 10))
  return [major, minor, patch]
}

// A release branch is named after a minor line with a literal `X` patch
// (e.g. "3.24.X"). KBE-683.
export const RELEASE_BRANCH_NAME_REGEX = /^\d+\.\d+\.X$/

export function isValidReleaseBranchName(value: string): boolean {
  return RELEASE_BRANCH_NAME_REGEX.test(value.trim())
}

/** The 3.X development line is the master; release branches are cut off it as
 * "3.<minor>.X". */
const RELEASE_BRANCH_MAJOR = 3

/** Suggests the next release-branch name by scanning existing network names
 * for the highest `3.<minor>` line and bumping the minor. Matches the minor
 * inside any name (e.g. "3.23.X", "Ancestralize 3.22.0"), so the suggestion
 * stays ahead of both release branches and their released patches. Falls back
 * to `3.0.X` when no 3.x line exists yet. See KBE-683. */
export function suggestNextReleaseBranchName(existingNames: string[]): string {
  const minorPattern = new RegExp(
    `(?:^|\\s)${RELEASE_BRANCH_MAJOR}\\.(\\d+)\\.`,
  )
  let maxMinor = -1
  for (const name of existingNames) {
    const match = name.match(minorPattern)
    if (match) maxMinor = Math.max(maxMinor, parseInt(match[1], 10))
  }
  return `${RELEASE_BRANCH_MAJOR}.${maxMinor + 1}.X`
}
